import React, { useEffect, useMemo, useState } from "react";
import { db } from "../firebase";
import { ref, onValue, update, remove, get, child } from "firebase/database";

function b64(s) { return btoa((s || "").toLowerCase()); }
function randomPin() { return String(Math.floor(1000 + Math.random() * 9000)); }
function makeVolId(name) {
  const base = (name || "VOL").replace(/[^A-Za-z0-9]/g, "").toUpperCase().slice(0, 4) || "VOL";
  const tail = Math.floor(100 + Math.random() * 900);
  return `${base}${tail}`;
}

export default function AdminVolunteers() {
  const [form, setForm] = useState({ email: "", name: "", pin: "", volId: "" });
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");

  // load volunteers
  useEffect(() => {
    const off = onValue(ref(db, "volunteers"), (snap) => {
      const v = snap.val() || {};
      const list = Object.entries(v).map(([id, o]) => ({ volId: id, ...o }));
      setRows(list.sort((a,b) => (b.createdAt||0)-(a.createdAt||0)));
    });
    return () => off();
  }, []);

  function onChange(k, val) {
    setForm(prev => ({ ...prev, [k]: val }));
  }

  async function addVolunteer() {
    const email = form.email.trim().toLowerCase();
    const name = form.name.trim();
    let pin = (form.pin || "").trim();
    let volId = (form.volId || "").trim().toUpperCase();

    if (!email || !email.includes("@")) return alert("Enter a valid email");
    if (!name) return alert("Enter name");
    if (!pin) pin = randomPin();
    if (!volId) volId = makeVolId(name);

    // Check duplicates
    const snap = await get(child(ref(db), `volunteers/${volId}`));
    if (snap.exists()) return alert(`volId ${volId} already exists, choose another.`);

    const now = Date.now();
    const up = {};
    up[`volunteers/${volId}`] = { email, name, pin, createdAt: now };
    up[`volunteersByEmail/${b64(email)}`] = { id: volId, name, pin };

    await update(ref(db), up);
    setForm({ email: "", name: "", pin: "", volId: "" });
  }

  async function removeVolunteer(volId, email) {
    if (!window.confirm(`Remove volunteer ${volId}?`)) return;
    const up = {};
    up[`volunteers/${volId}`] = null;
    if (email) up[`volunteersByEmail/${b64(email)}`] = null;
    await update(ref(db), up);
  }

  async function saveRow(r) {
    if (!r.volId) return alert("Missing volId");
    const email = (r.email || "").toLowerCase();
    if (!email || !email.includes("@")) return alert("Enter valid email");
    if (!r.name) return alert("Enter name");
    if (!r.pin) return alert("Enter PIN");

    // If email changed, we must update index node too.
    // Find the previous email from DB (to clean old index if changed)
    const oldSnap = await get(ref(db, `volunteers/${r.volId}`));
    const old = oldSnap.val() || {};
    const up = {};
    up[`volunteers/${r.volId}`] = {
      email, name: r.name, pin: r.pin, createdAt: old.createdAt || Date.now()
    };
    // remove old index if email changed
    if (old.email && old.email !== email) {
      up[`volunteersByEmail/${b64(old.email)}`] = null;
    }
    // write new index
    up[`volunteersByEmail/${b64(email)}`] = { id: r.volId, name: r.name, pin: r.pin };
    await update(ref(db), up);
    alert("Saved");
  }

  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.volId || "").toLowerCase().includes(t) ||
      (r.email || "").toLowerCase().includes(t) ||
      (r.name || "").toLowerCase().includes(t)
    );
  }, [rows, q]);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl md:text-3xl font-bold">Admin — Volunteers</h1>

      {/* Add form */}
      <div className="bg-white rounded-2xl shadow p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <input className="p-3 border rounded-xl" placeholder="Volunteer email"
                 value={form.email} onChange={(e)=>onChange("email", e.target.value)} />
          <input className="p-3 border rounded-xl" placeholder="Name"
                 value={form.name} onChange={(e)=>onChange("name", e.target.value)} />
          <input className="p-3 border rounded-xl" placeholder="PIN (4-6 digits, auto if empty)"
                 value={form.pin} onChange={(e)=>onChange("pin", e.target.value)} />
          <input className="p-3 border rounded-xl" placeholder="volId (auto if empty)"
                 value={form.volId} onChange={(e)=>onChange("volId", e.target.value)} />
        </div>
        <div className="flex gap-3">
          <button onClick={addVolunteer}
                  className="px-4 py-3 rounded-xl bg-black text-white hover:opacity-90">Add Volunteer</button>
          <input className="ml-auto p-3 border rounded-xl" placeholder="Search email/name/volId"
                 value={q} onChange={(e)=>setQ(e.target.value)} />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow p-6 overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-2 pr-4">volId</th>
              <th className="py-2 pr-4">Email</th>
              <th className="py-2 pr-4">Name</th>
              <th className="py-2 pr-4">PIN</th>
              <th className="py-2 pr-4">Created</th>
              <th className="py-2 pr-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <EditableRow key={r.volId} row={r} onSave={saveRow} onRemove={removeVolunteer} />
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="6" className="py-6 text-gray-500">No volunteers found.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditableRow({ row, onSave, onRemove }) {
  const [e, setE] = useState(row);
  useEffect(() => setE(row), [row]);

  return (
    <tr className="border-b hover:bg-gray-50">
      <td className="py-2 pr-4 font-mono">{e.volId}</td>
      <td className="py-2 pr-4">
        <input className="p-2 border rounded-lg w-64"
               value={e.email || ""} onChange={(ev)=>setE({ ...e, email: ev.target.value })} />
      </td>
      <td className="py-2 pr-4">
        <input className="p-2 border rounded-lg w-48"
               value={e.name || ""} onChange={(ev)=>setE({ ...e, name: ev.target.value })} />
      </td>
      <td className="py-2 pr-4">
        <input className="p-2 border rounded-lg w-28"
               value={e.pin || ""} onChange={(ev)=>setE({ ...e, pin: ev.target.value })} />
      </td>
      <td className="py-2 pr-4 text-gray-500">
        {e.createdAt ? new Date(e.createdAt).toLocaleString() : "-"}
      </td>
      <td className="py-2 pr-4 space-x-2">
        <button onClick={()=>onSave(e)}
                className="px-3 py-1 rounded-lg border hover:bg-gray-50">Save</button>
        <button onClick={()=>onRemove(e.volId, e.email)}
                className="px-3 py-1 rounded-lg border hover:bg-red-50 text-red-600">Delete</button>
      </td>
    </tr>
  );
}
